"use client";

"use client";

import { useState, useRef, useCallback } from 'react';
import ChatBoxView from "../components/chat/chatview";
import { IChatMessage } from "@/types/chat-types";
import { chatService } from '../services/chatService';
import { useLocation } from 'react-router-dom';
import ModernChat from '../components/chat/modern/ModernChat';

const ChatWindow = () => {
    const location = useLocation();
    const playbookId = location.state?.playbookId;
    const playbookName = location.state?.playbookName;
    const sessionId = location.state?.sessionId;
    console.log("playbookId", playbookId);
    
    // Use modern chat interface by default, unless specifically coming from a different playbook
    const MODERN_CHAT_PLAYBOOK_ID = 'PLAYBOOK-cd762b91-8176-4924-9a92-d2ccfc089488';
    const useModernInterface = !playbookId || playbookId === MODERN_CHAT_PLAYBOOK_ID;
    const [messages, setMessages] = useState<IChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const [userInput, setUserInput] = useState(null);

    const handleSendMessage = useCallback(async (message?: string) => {
        if (!message?.trim()) return;

        try {
            if (message != "Start the execution") {
                // Add user message to the chat
                const userMessage: IChatMessage = {
                    id: `user-${Date.now()}`,
                    text: message,
                sender: 'user',
                timestamp: new Date().toISOString(),
                };

                setMessages(prev => [...prev, userMessage]);

                // save message in localStorage
                let messages = JSON.parse(localStorage.getItem('messages') || '[]');
                messages.push(userMessage);
                localStorage.setItem('messages', JSON.stringify(messages));

            }
            setIsLoading(true);

            // Create a new AbortController for this request
            const controller = new AbortController();
            abortControllerRef.current = controller;

            // Clear any previous cleanup
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }

            try {
                if (!playbookId) {
                throw new Error('Playbook ID is required');
            }

            const cleanup = await chatService.sendMessage(
                message,
                userInput,
                playbookId.toString(),
                sessionId,
                messages,
                (chunk: any, isEvent: boolean, eventType?: string) => {
                    console.log('Received chunk:', chunk, 'isEvent:', isEvent, 'eventType:', eventType);
                    if(eventType == 'tool' && chunk?.["type"] == "USER_INPUT"){
                        setUserInput(chunk);
                        return;
                    }
                    setMessages(prev => {
                        const newMessages = [...prev];
                        // For events, create a new message
                        if (isEvent) {
                            let eventMessage: IChatMessage;
                            console.log("chunk", typeof chunk, chunk);
                            if(eventType == 'tool' && chunk?.["validation"] == "SUCCESS"){
                                eventMessage = {
                                    id: `sys-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    text: "Validation Successfull for " + chunk?.["slot_id"],
                                    sender: 'assistant',
                                    timestamp: new Date().toISOString(),
                                };
                                // add this message to localStorage
                                let messages = JSON.parse(localStorage.getItem('messages') || '[]');
                                messages.push(eventMessage);
                                localStorage.setItem('messages', JSON.stringify(messages));
                            }
                            else {
                                eventMessage = {
                                    id: `sys-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    text: chunk,
                                    sender: 'assistant',
                                    timestamp: new Date().toISOString(),
                                };
                                // add this message to localStorage
                                let messages = JSON.parse(localStorage.getItem('messages') || '[]');
                                messages.push(eventMessage);
                                localStorage.setItem('messages', JSON.stringify(messages));
                            }
                            return [...newMessages, eventMessage];
                        }
                        
                        // For streaming text, update the last message
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.sender === 'assistant') {
                            lastMessage.text = chunk;
                            lastMessage.timestamp = new Date().toISOString();
                        } else {
                            // If no bot message exists, create one
                            newMessages.push({
                                id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                text: chunk,
                                sender: 'assistant',
                                timestamp: new Date().toISOString()
                            });
                        }
                        return [...newMessages];
                    });
                },
                () => {
                    // On complete
                    setIsLoading(false);
                    cleanupRef.current = null;
                },
                (error: Error) => {
                    console.error('Error in chat service:', error);
                    let eventMessage: IChatMessage = {
                        id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        text: `Error: ${error.message}`,
                        sender: 'assistant',
                        timestamp: new Date().toISOString(),
                    }
                    setMessages(prev => [...prev, eventMessage]);
                    // add this message to localStorage
                    let messages = JSON.parse(localStorage.getItem('messages') || '[]');
                    messages.push(eventMessage);
                    localStorage.setItem('messages', JSON.stringify(messages));
                    setIsLoading(false);
                    cleanupRef.current = null;
                },
                setUserInput,
                controller.signal
                );

                cleanupRef.current = cleanup;
            } catch (error: unknown) {
                console.error('Error starting chat service:', error);
                let eventMessage: IChatMessage = {
                    id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    text: `Error: ${error instanceof Error ? error.message : 'Failed to start chat'}`,
                    sender: 'assistant',
                    timestamp: new Date().toISOString(),
                }
                setMessages(prev => [...prev, eventMessage]);

                // add this message to localStorage
                let messages = JSON.parse(localStorage.getItem('messages') || '[]');
                messages.push(eventMessage);
                localStorage.setItem('messages', JSON.stringify(messages));
                setIsLoading(false);
                cleanupRef.current = null;
            }
        } catch (error: unknown) {
            console.error('Error sending message:', error);
            let eventMessage: IChatMessage = {
                id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                text: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
                sender: 'assistant',
                timestamp: new Date().toISOString(),
            }
            setMessages(prev => [...prev, eventMessage]);
            // add this message to localStorage
            let messages = JSON.parse(localStorage.getItem('messages') || '[]');
            messages.push(eventMessage);
            localStorage.setItem('messages', JSON.stringify(messages));
            setIsLoading(false);
        }
    }, [userInput]);

    const handleStopGeneration = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    }, []);

    // Render modern chat interface by default
    if (useModernInterface) {
        return (
            <ModernChat 
                playbookId={playbookId || MODERN_CHAT_PLAYBOOK_ID} 
                playbookName={playbookName || 'LoanForeclosureWorkflow'}
            />
        );
    }

    // Render original chat interface for other playbooks
    return (
        <main style={{ height: "94vh", overflow: "hidden" }} className="container mx-auto p-4">
            <div className="mb-1 ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Chat with Agent
                </h1>
                <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1 mb-6">
                    Chat with your agent about this playbook.
                </p>
            </div>
            <ChatBoxView 
                initMessages={messages} 
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
                onStopGeneration={handleStopGeneration}
                userInput={userInput}
                setUserInput={setUserInput}
            />
        </main>
    );
};

export default ChatWindow;